import React, { useEffect, useRef, useMemo } from "react";
import { useI18n } from "Hooks";
import _ from "lodash";
import { connect } from "react-redux";
import classNames from "classnames";

import FormTextInput from "UIComponents/InputTextField/FormTextInput";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import AIGradingActions from "AppComponents/AIGradingActions";

import {
  Dropdown,
  DropdownMenu,
  DSProvider,
  IconButton,
  ToggleSwitch,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  DashboardOutlined,
  DotsHorizontalOutlined,
  ReloadArrowOutlined,
} from "@toddle-edu/ds-icons";

import { getSettingValue } from "Utils/ConfigurationUtils";
import {
  updateScoreAssessmentTool,
  updateEvaluationData,
} from "ClassRoom/modules/ClassRoomModule";
import { updateSettings } from "modules/ConfigurationThunks";

import { getCalculatedScore } from "AppComponents/StandardRubric/modules/Utils";
import { getStandardRubricCalculatedScore } from "ClassRoom/modules/utils";

import {
  CODE,
  STANDARD_RUBRIC_DISPLAY_SETTINGS,
} from "AppComponents/StandardRubric/modules/Constants";
import { STAFF_USER } from "Constants/stringConstants";
import {
  CRITERIA_DESCRIPTION,
  RATING_DESCRIPTION,
  SPLIT_VIEW_DESCRIPTION,
} from "../../modules/Constants";

import classes from "./OverrideStandardRubricScore.scss";

const DESCRIPTION_OPTIONS = [CODE, CRITERIA_DESCRIPTION, RATING_DESCRIPTION];

const styles = {
  inputStyle: {
    width: "56px",
  },
  overriddenInputStyle: {
    width: "56px",
    backgroundColor: "var(--decorative-background-subtle-yellow)",
  },
  containerStyle: {
    width: "min-content",
  },
  overlayInnerStyle: {
    maxWidth: "142px",
  },
  aiSelectedScore: {
    width: "56px",
    border: "1px dashed var(--interactive-tertiary)",
    backgroundColor: `var(--decorative-background-subtle-violet)`,
  },
};

const tooltipAlign = {
  offset: [13, 0],
};

const OverrideStandardRubricScore = props => {
  const {
    scoreValue, // actual score (could be overridden)
    assessmentTool,
    isEvaluationEnable,
    studentDetails,
    studentAssignmentId,
    academicTermId,
    sourceFrom,
    updateScoreAssessmentTool,
    updateEvaluationData,
    toolResponses,
    showStandardRubricDescription,
    setIBPYPElementRatingsLocal,
    updateScoreLocal,
    isAutoSaveDisabled,
    testId = "appComponents-overrideStandardRubricScore",
    updateSettings,
    type,
    isGeneratingAIResponse,
    isAISelectedScore,
    onAcceptAIEvaluation,
    isAiEvaluationEnabled,
    isEvaluated,
    ibPYPElementRatings,
    assessmentTools,
    showDescriptionToggle = true,
    showOverrideScore = true,
  } = props;

  const settingName = STANDARD_RUBRIC_DISPLAY_SETTINGS[type];

  // hooks
  const { t } = useI18n();

  // refs
  const inputRef = useRef(null);

  // side effects
  const userPreferredDisplayOptions = getSettingValue({
    name: settingName,
    readFromUserAndOrgLevel: true,
  });

  useEffect(() => {
    if (!showOverrideScore) return;
    const showStandardRubricDescription = _.includes(
      userPreferredDisplayOptions,
      SPLIT_VIEW_DESCRIPTION
    );

    updateEvaluationData({ showStandardRubricDescription });
  }, [showOverrideScore]);

  // variables
  const associateScoreTool = _.get(assessmentTool, "associateScoreTool");
  const metadata = _.get(assessmentTool, "metadata");
  const maxScore = _.get(associateScoreTool, "scoreToolData.maxScore");

  const calculatedScore = useMemo(
    () =>
      getCalculatedScore({
        toolResponses,
        assessmentTool,
        isAiEvaluationEnabled,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toolResponses]
  );

  const scaledScore = getStandardRubricCalculatedScore({
    assessmentTool,
    calculatedScore,
  });

  const hasStaffResponses = !_.isEmpty(
    _.find(toolResponses, { creatorType: STAFF_USER })
  );

  const isScoreOverridden =
    (hasStaffResponses || !_.isEmpty(scoreValue)) && scoreValue !== scaledScore;

  const disableScoreTool = !isEvaluationEnable || _.isEmpty(maxScore);

  const style = useMemo(() => {
    if (isAISelectedScore) {
      return styles.aiSelectedScore;
    }

    if (isScoreOverridden) {
      return styles.overriddenInputStyle;
    }

    return styles.inputStyle;
  }, [isAISelectedScore, isScoreOverridden]);

  const showToggleDescriptionSwitch = useMemo(() => {
    const criteria = _.get(assessmentTool, "standardRubricCriterias", []);
    const selectedDisplayOptions = _.get(
      metadata,
      "selectedDisplayOptions",
      []
    );

    const hasDescriptions = _.some(criteria, criterion => {
      // Check criterion description
      if (!_.isEmpty(_.trim(_.get(criterion, "description")))) return true;

      // Check descriptor descriptions
      const descriptors = _.get(criterion, "descriptors", []);

      return _.some(descriptors, descriptor => {
        const { description, standardGradeScaleValueDescriptor } =
          descriptor || {};

        const descriptorDescription =
          description || standardGradeScaleValueDescriptor?.description;

        const hasDescriptorDescription = !_.isEmpty(
          _.trim(descriptorDescription)
        );

        return hasDescriptorDescription;
      });
    });

    return (
      hasDescriptions &&
      (_.isNil(metadata) ||
        _.some(selectedDisplayOptions, option =>
          _.includes(DESCRIPTION_OPTIONS, option)
        ))
    );
  }, [assessmentTool, metadata]);

  const showOverrideInput = !_.isEmpty(maxScore);

  const isAIEvaluationExist =
    _.some(toolResponses, { creatorType: "AI" }) ||
    _.some(ibPYPElementRatings, { creatorType: "AI" });

  const showAIActions =
    isAiEvaluationEnabled &&
    !isEvaluated &&
    isAIEvaluationExist &&
    !isGeneratingAIResponse;

  // functions

  const updateScore = score => {
    const input = {
      score,
      maxScore,
      studentDetails,
      studentAssignmentId,
      academicTermId,
      sourceFrom,
      setIBPYPElementRatingsLocal,
      updateScoreLocal,
      isAutoSaveDisabled,
      assessmentTools,
    };

    if (isAISelectedScore) {
      input.isDeleteAiRatings = true;
    }

    updateScoreAssessmentTool(input);
  };

  const acceptAIEvaluatedScore = () => {
    if (isAISelectedScore) {
      updateScore(scaledScore);
      onAcceptAIEvaluation({ academicTermId });
    }
  };

  const onClickResetScore = () => {
    if (isAISelectedScore) {
      acceptAIEvaluatedScore();
    } else {
      updateScore(hasStaffResponses ? scaledScore : "");
    }
  };

  const onChangeShowDescription = e => {
    try {
      const isChecked = e.target.checked;

      updateEvaluationData({ showStandardRubricDescription: isChecked });

      let updatedUserPreferredDisplayOptions = [...userPreferredDisplayOptions];

      if (!isChecked) {
        updatedUserPreferredDisplayOptions = _.filter(
          updatedUserPreferredDisplayOptions,
          option => option !== SPLIT_VIEW_DESCRIPTION
        );
      } else {
        updatedUserPreferredDisplayOptions = [
          ...updatedUserPreferredDisplayOptions,
          SPLIT_VIEW_DESCRIPTION,
        ];
      }

      updateSettings({
        settings: [
          {
            name: settingName,
            value: JSON.stringify(updatedUserPreferredDisplayOptions),
          },
        ],
        showToastMsg: false,
      });
    } catch (error) {
      // do nothing
    }
  };

  const renderDropdownDescriptionToggle = () => {
    return (
      <Dropdown
        dsVersion="2.0"
        overlay={
          <DropdownMenu
            dsVersion="2.0"
            options={[
              {
                key: "show_description",
                label: t("common:show_description"),
                icon: <DashboardOutlined />,
                suffix: (
                  <ToggleSwitch
                    size={"medium"}
                    checked={showStandardRubricDescription}
                    onChange={onChangeShowDescription}
                    testId={`${testId}-show-description-toggleSwitch`}
                  />
                ),
              },
            ]}
          />
        }
      >
        <IconButton
          dsVersion="2.0"
          icon={<DotsHorizontalOutlined />}
          variant="neutral"
          type="plain"
          size="medium"
        />
      </Dropdown>
    );
  };

  // classes

  const inputContainerClass = classNames({
    [classes.inputContainer]: true,
    [classes.aiSelectedScoreInputContainer]: isAISelectedScore,
    [classes.overriddenScoreInputContainer]: isScoreOverridden,
  });

  const tooltipText = isAISelectedScore
    ? t("ai:click_to_approve_score")
    : t("classRoom:calculated_score_overridden_with_label", {
        score: scaledScore,
      });

  if (!showOverrideScore) return null;

  return (
    <DSProvider dsVersion="2.0">
      <div className={classes.container}>
        {showDescriptionToggle &&
        showToggleDescriptionSwitch &&
        !showAIActions &&
        !isGeneratingAIResponse ? (
          <div className={classes.descriptionSettingContainer}>
            <span className={classNames("text-body", classes.label)}>
              {t("common:show_description")}
            </span>
            <ToggleSwitch
              size={"medium"}
              checked={showStandardRubricDescription}
              onChange={onChangeShowDescription}
              testId={`${testId}-show-description-toggleSwitch`}
            />
          </div>
        ) : null}

        {showOverrideInput ? (
          <div className={inputContainerClass}>
            {isGeneratingAIResponse ? (
              <SkeletonLoader width="56px" height="36px" />
            ) : (
              <div className={classes.input} ref={inputRef}>
                <Tooltip
                  tooltip={tooltipText}
                  placement={"bottomLeft"}
                  visible={isScoreOverridden}
                >
                  <FormTextInput
                    dsVersion={"2.0"}
                    size={"medium"}
                    value={scoreValue}
                    style={style}
                    containerStyle={styles.containerStyle}
                    placeholder={"-"}
                    disabled={disableScoreTool}
                    updateInputField={updateScore}
                    testId={`${testId}-score-input`}
                    onFocusInputField={acceptAIEvaluatedScore}
                    customErrorClass={classes.customErrorClass}
                  />
                </Tooltip>

                {isScoreOverridden && !disableScoreTool ? (
                  <Tooltip
                    tooltip={t(
                      "classRoom:click_to_reset_rubric_calculated_score"
                    )}
                    placement={"topRight"}
                    overlayInnerStyle={styles.overlayInnerStyle}
                    align={tooltipAlign}
                    showArrow={true}
                  >
                    <IconButton
                      icon={<ReloadArrowOutlined />}
                      variant={"neutral"}
                      type={"outlined"}
                      size={"x-small"}
                      dsVersion="2.0"
                      className={classes.resetScoreButton}
                      onClick={onClickResetScore}
                      testId={`${testId}-reset-score-iconButton`}
                    />
                  </Tooltip>
                ) : null}
              </div>
            )}

            <span
              className={classNames(classes.maxScore, "text-body")}
            >{`/ ${maxScore}`}</span>
          </div>
        ) : null}

        {showAIActions ? (
          <div className={classes.aiActionsContainer}>
            {showOverrideInput ? <span className={classes.separator} /> : null}
            <AIGradingActions
              onAcceptAIEvaluation={onAcceptAIEvaluation}
              academicTermId={academicTermId}
              assessmentToolType={type}
              toolResponses={toolResponses}
              ibPYPElementRatings={ibPYPElementRatings}
            />
            {renderDropdownDescriptionToggle()}
          </div>
        ) : null}
      </div>
    </DSProvider>
  );
};

OverrideStandardRubricScore.displayName = "OverrideStandardRubricScore";

const mapStateToProps = state => {
  const showStandardRubricDescription = _.get(
    state,
    "classRoom.evaluation.showStandardRubricDescription",
    true
  );

  return {
    showStandardRubricDescription,
  };
};

const mapActionCreators = {
  updateSettings,
  updateEvaluationData,
  updateScoreAssessmentTool,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(OverrideStandardRubricScore);
